// Third party dependencies
import {
  Box,
  Button,
  CopyToClipboard,
  Error as ErrorComponent,
  Gap,
  Header,
  Table,
  Toggle,
  Notification,
  ISelectOption,
} from '@salecycle/ui-component-library';
import { useReducer, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { Column } from 'react-table';

// Local dependencies
import { useClients, usePermissions } from 'core/app/AppContext';
import { RestApiError } from 'core/network/RestApi';
import * as RestApi from 'core/network/RestApi';
import { ClientLevel } from 'core/types';
import { NotAuthorisedPage } from 'core/ui/components/error';
import Spinner from 'core/ui/components/spinner';
import { FileManagementRoute } from 'sections/file-management/routes';
import { IDbFileExportConfig, IFileExportConfig, IFileExportConfigFolderUpdate, IFileExportFilters } from '../entities';
import { IState, fileExportTableReducer } from '../reducer/fileExportTableReducer';
import { getFolderLocationInfo } from './FileExportFolderWithCopy';
import { EditButton } from 'sections/user-management/sections/roles/sections/view-roles/components/EditButton';
import FileExportFilters from './FileExportFilters';
import ConfirmationModal from 'components/experimental/ConfirmationModal';

const { SITE, GROUP, BRAND } = ClientLevel;
const { FILE_EXPORT_CONFIG_CREATE, FILE_EXPORT_CONFIG_EDIT } = FileManagementRoute;
export interface IProps {
  clientLevel: string;
  clientId: string;
}

const initialState: IState = {
  isError: false,
  errorMessage: '',
  pageNumber: 0,
  data: [],
  loadingData: true,
};

interface ITableValue {
  original: IFileExportConfig;
  value: any;
}

interface ITableFilter {
  id: string;
  value: string | boolean;
}

export const FileExportTable = ({ clientLevel, clientId }: IProps) => {
  const [{ pageNumber }, dispatch] = useReducer(fileExportTableReducer, initialState);
  const { getSite, getBrand, getGroup } = useClients();
  const { t } = useTranslation('fileExport');
  const { hasPermission } = usePermissions();
  const { push } = useHistory();
  const [modalOptions, setModalOptions] = useState({ heading: '', description: '', onOkButtonClicked: null });
  const [errorMessage, setErrorMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({
    enabledFolders: true,
  });
  const [fileExportConfigs, setFileExportConfigs] = useState([]);

  const getConfigQuery = useQuery(['get-file-export-config', clientId], async () => {
    if (!clientId) return null;
    const result = await RestApi.get<IDbFileExportConfig[]>(`/${clientLevel}s/${clientId}/file_export`);
    return result.data;
  });

  useEffect(() => {
    if (getConfigQuery.isLoading) {
      return;
    }

    const configs = (getConfigQuery.data ?? []).map((row: IDbFileExportConfig) => ({
      folder: row?.folder,
      timestampFormatActive: row?.transformations?.timestampFormat?.active,
      timestampRemovalActive: row?.transformations?.timestampRemoval?.active,
      compressionActive: row?.transformations?.compression?.active,
      enabled: row?.enabled,
    }));
    setFileExportConfigs(configs);
  }, [getConfigQuery.isLoading, getConfigQuery.data]);

  const putFolderEnabled = useMutation((folderUpdate: IFileExportConfigFolderUpdate) => {
    return RestApi.put(`/${clientLevel}s/${clientId}/file_export/${folderUpdate.folder}/status`, folderUpdate);
  });

  const updateFilters = (value: string | boolean | ISelectOption<string>, type: string): void => {
    setFilters({ ...filters, [type]: value });
  };

  const handleFilterChange = (value: string | boolean | ISelectOption<string>, type: string): void => {
    updateFilters(value, type);
  };

  const defaultFilterMethod = (filter: ITableFilter, row: IFileExportConfig): boolean => customFilter(filter, row);

  const customFilter = (filter: ITableFilter, row: IFileExportConfig): boolean => {
    const { id, value } = filter;

    if (row[id] !== undefined) {
      switch (id) {
        case 'enabled':
          return !filter.value || row.enabled;
      }
    } else if (value !== '') {
      return false;
    }

    return true;
  };

  const getClient =
    clientLevel === SITE
      ? getSite
      : clientLevel === BRAND
      ? getBrand
      : clientLevel === GROUP
      ? getGroup
      : () => ({ name: null });

  const client = getClient(clientId);
  if (!client) return null;
  const clientName = client.name;

  const toggleFolder = async (folder: string, enabled: boolean): Promise<void> => {
    putFolderEnabled.mutate(
      { folder, enabled },
      {
        onSuccess: () => {
          // Only update the folder if the backend call is successful
          const clientFolder = fileExportConfigs.find(u => u.folder === folder);
          clientFolder.enabled = enabled;
        },
        onError: (error: RestApiError, variables, context) => {
          setErrorMessage(error.message);
        },
      },
    );
  };

  const handleToggleFolderClick = (folder: string, enabled: boolean) => {
    setModalOptions({
      description: t('table.toggleFolder.modal.description', {
        context: enabled ? 'enabled' : filters.enabledFolders ? 'disabledHidden' : 'disabled',
        folder,
      }),
      heading: t('table.toggleFolder.modal.heading', {
        context: enabled ? 'enabled' : 'disabled',
      }),
      onOkButtonClicked: () => {
        closeModal();
        toggleFolder(folder, enabled);
      },
    });

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const getColumns = (
    handleToggleFolderClick: (folder: string, checked: boolean) => void,
    filters: IFileExportFilters,
    includeTransformations: boolean,
  ): Column[] => {
    const CopyButton = (props: ITableValue): JSX.Element => {
      const { clipboardValue } = getFolderLocationInfo(clientId, props.original.folder);
      return <CopyToClipboard value={clipboardValue} showIconOnly={true} />;
    };

    const EditButtonChange = (props: ITableValue): JSX.Element => {
      return (
        <EditButton
          onClick={() =>
            push(FILE_EXPORT_CONFIG_EDIT.replace(':clientId', clientId).replace(':folderName', props.original.folder), {
              timestampRemovalActive: props.original.timestampRemovalActive,
              timestampFormatActive: props.original.timestampFormatActive,
              compressionActive: props.original.compressionActive,
              clientLevel,
              clientName,
            })
          }
        />
      );
    };

    const EditButtons = (props: ITableValue): JSX.Element => {
      const showEdit = !props.original.folder.includes('/');
      return (
        <>
          <Box horizontalPositioning="end">
            <CopyButton {...props} />
            <Gap right={10} />
            {showEdit && <EditButtonChange {...props} />}
          </Box>
        </>
      );
    };

    const StatusToggle = (props: ITableValue): JSX.Element => {
      const isChecked = props.original.enabled;
      const showToggle = !props.original.folder.includes('/');

      return (
        <>
          {showToggle && (
            <Toggle
              checked={isChecked}
              margin={false}
              onChange={(checked: boolean): void => handleToggleFolderClick(props.original.folder, checked)}
              ariaLabel={t('table.toggleFolder.label', {
                context: isChecked ? 'enabled' : 'disabled',
                folder: props.original.folder,
              })}
            />
          )}
        </>
      );
    };

    return [
      {
        Header: t('table.columns.folderName'),
        accessor: 'folder',
        id: 'folder',
        Cell: undefined,
      },
      {
        Header: t('table.columns.enabled'),
        accessor: (d: IFileExportConfig): boolean => !filters.enabledFolders || d.enabled,
        id: 'enabled',
        Cell: StatusToggle,
      },
    ]
      .concat(
        includeTransformations
          ? [
              {
                Header: t('table.columns.compression'),
                accessor: 'compressionActive',
                id: 'compression',
                Cell: (props: ITableValue): string => (props.value ? 'Active' : 'Inactive'),
              },
              {
                Header: t('table.columns.timestampFormat'),
                accessor: 'timestampFormatActive',
                id: 'timestampFormat',
                Cell: (props: ITableValue): string => (props.value ? 'Active' : 'Inactive'),
              },
              {
                Header: t('table.columns.timestampRemoval'),
                accessor: 'timestampRemovalActive',
                id: 'timestampRemoval',
                Cell: (props: ITableValue): string => (props.value ? 'Active' : 'Inactive'),
              },
            ]
          : [],
      )
      .concat([
        {
          Header: '',
          accessor: 'edit',
          id: 'edit',
          Cell: EditButtons,
        },
      ]);
  };

  const handleNotificationClose = (isOpen: boolean): void => {
    if (!isOpen) {
      setErrorMessage('');
    }
  };

  const hasManageClientsPermission = hasPermission('ManageClients');
  const notAuthorised = !hasManageClientsPermission;
  const showError = getConfigQuery.isError;
  const showSpinner = getConfigQuery.isLoading;
  const showTable = !showError && !notAuthorised && !showSpinner;

  return (
    <>
      <Notification isOpen={!!errorMessage} variation="error" onChange={handleNotificationClose}>
        {errorMessage}
      </Notification>

      <ConfirmationModal
        cancelButtonLabel={t('table.toggleFolder.modal.cancel')}
        description={modalOptions.description}
        heading={modalOptions.heading}
        isOpen={showModal}
        okButtonLabel={t('table.toggleFolder.modal.ok')}
        onCancelButtonClicked={closeModal}
        onCloseButtonClicked={closeModal}
        onOkButtonClicked={modalOptions.onOkButtonClicked}
      />

      <Box horizontalPositioning="space-between">
        <Header heading={t('table.heading', { clientName })} />
        <Button
          variation="primary"
          onClick={() => push(FILE_EXPORT_CONFIG_CREATE.replace(':clientId', clientId), { clientLevel, clientName })}
          ariaLabel={t('table.button')}
        >
          {t('table.button')}
        </Button>
      </Box>
      {showError && (
        <div>
          <ErrorComponent
            heading={t('error.heading')}
            description={(getConfigQuery.error as Error)?.message || t('error.description')}
          />
        </div>
      )}
      <FileExportFilters handleFilterChange={handleFilterChange} filters={filters} />
      {!showError && notAuthorised && <NotAuthorisedPage />}
      {showSpinner && <Spinner />}
      {showTable && (
        <Table
          columns={getColumns(handleToggleFolderClick, filters, true)}
          data={fileExportConfigs}
          filtered={[{ id: 'enabled', value: filters.enabledFolders }]}
          defaultFilterMethod={defaultFilterMethod}
          defaultPageSize={10}
          page={pageNumber}
          onPageChange={(pageNumber: number) => {
            dispatch({ type: 'UPDATE_PAGE_NUMBER', payload: { pageNumber } });
          }}
          onPageSizeChange={(_: number, pageNumber: number) => {
            dispatch({ type: 'UPDATE_PAGE_NUMBER', payload: { pageNumber } });
          }}
          onSortedChange={() => {
            dispatch({ type: 'UPDATE_PAGE_NUMBER', payload: { pageNumber: 0 } });
          }}
          noDataText={t('table.noData')}
        />
      )}
    </>
  );
};
